import { useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { Link, useNavigate } from 'react-router-dom';
import * as ROUTES from './../routes'

import LoadingComponent from '../Components/LoadingComponent';

import * as TYPES from './types';
import { withReduxAuth } from '../Redux/indexAuth';

const LoginModal: React.FC<TYPES.LoginModalProps> = (props: TYPES.LoginModalProps) => {
    const { show, onHide, auth, authLogin, authClearError, authSetError } = props;
    const navigate = useNavigate();

    // Close modal and redirect on successful login
    useEffect(() => {
        if (show && auth.authToken?.length) {
            navigate(ROUTES.PATH.LANDING);
        }
    }, [auth.authToken, navigate, show]);

    // Add class to body when modal is open to prevent background scrolling and adjust styles
    useEffect(() => {
        if (show) {
            document.body.classList.add('gn-login-modal-open');
        } else {
            document.body.classList.remove('gn-login-modal-open');
        }
        return () => { document.body.classList.remove('gn-login-modal-open'); };
    }, [show]);

    // google login handlers
    const handleGoogleLoginErrorCallback = () => {
        authSetError('Google sign-in failed. Please try again later.');
    };

    const handleGoogleLoginCallback = (response: CredentialResponse) => {
        if (response.credential) {
            authLogin(response.credential);
        } else {
            handleGoogleLoginErrorCallback();
        }
    };

    return (
        <Modal
            show={show}
            dialogClassName="gn-login-modal"
            aria-labelledby="gn-login-modal-title"
            centered
            onHide={() => { if (!auth.httpInProgress) { authClearError(); onHide?.(); } }}
        >
            <Modal.Body>
                {/* Close button */}
                <Button
                    className="btn btn-secondary theme-tools-transparent-button-icon-sized gn-login-modal-close"
                    title="Close"
                    onClick={() => { authClearError(); onHide?.(); }}
                    disabled={auth.httpInProgress}
                >
                    <svg style={{ width: 9, height: 9, display: 'block', flexShrink: 0 }} viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                </Button>

                <div className="d-flex flex-column align-items-center pt-2 pb-3 px-2" style={{ gap: 16 }}>

                    {/* Early access badge */}
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7,
                        background: 'rgba(255,165,0,.1)', border: '1px solid rgba(255,165,0,.3)',
                        borderRadius: 100, padding: '4px 14px',
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                        color: 'orange',
                    }}>
                        <LoadingComponent type="pulse-dot" />
                        Early Access
                    </div>

                    <h2 id="gn-login-modal-title" style={{ color: '#fff', fontWeight: 800, fontSize: 20, letterSpacing: '-0.02em', margin: 0, lineHeight: 1.25, textAlign: 'center' }}>
                        Welcome to Game Nostic
                    </h2>

                    <p className="text-center mb-0" style={{ color: '#6f6f6b', fontSize: 14, lineHeight: 1.6 }}>
                        You're joining an early access build. The core features are live and running —
                        but you may encounter rough edges along the way.
                    </p>

                    {/* Notices */}
                    <div className="w-100 d-flex flex-column" style={{ gap: 8 }}>
                        {[
                            { icon: '⚠️', text: 'Some features may be incomplete or behave unexpectedly during early access.' },
                            { icon: '🔄', text: 'Accounts and project data may be reset as we iterate on the platform.' },
                            { icon: '💬', text: 'Your feedback directly shapes what gets built. We\'re listening.' },
                        ].map(({ icon, text }) => (
                            <div key={icon} style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                background: '#1d1d27', border: '1px solid #6f6f6b',
                                borderRadius: 8, padding: '9px 13px',
                            }}>
                                <span style={{ fontSize: 13, flexShrink: 0 }}>{icon}</span>
                                <span style={{ fontSize: 12, color: '#6f6f6b', lineHeight: 1.5 }}>{text}</span>
                            </div>
                        ))}
                    </div>

                    <hr className="w-100 mt-1 mb-1" style={{ borderColor: 'rgba(255,255,255,.07)' }} />

                    {auth.httpInProgress ? (
                        <div className="d-flex flex-column align-items-center" style={{ gap: 12 }}>
                            <LoadingComponent type="flex-centered" />
                            <span style={{ color: '#6f6f6b', fontSize: 13 }}>Signing you in…</span>
                        </div>
                    ) : auth.httpError ? (
                        <div className="d-flex flex-column w-100" style={{ gap: 12 }}>
                            <div className="d-flex align-items-center w-100" style={{ gap: 10 }}>
                                <svg width="28" height="26" viewBox="0 0 42 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                    <path d="M19.268 2C20.038 0.667 21.962 0.667 22.732 2L40.856 33C41.626 34.333 40.664 36 39.124 36H2.876C1.336 36 0.374 34.333 1.144 33L19.268 2Z"
                                        fill="rgba(220,53,69,.08)" stroke="rgba(220,53,69,.4)" strokeWidth="1.5"/>
                                    <path d="M21 13v11" stroke="#dc3545" strokeWidth="2.2" strokeLinecap="round"/>
                                    <circle cx="21" cy="29" r="1.5" fill="#dc3545"/>
                                </svg>
                                <div
                                    className="d-flex align-items-center w-100 text-center"
                                    style={{
                                        color: '#f08080', fontSize: 13, lineHeight: 1.6,
                                        background: 'rgba(220,53,69,.08)', border: '1px solid rgba(220,53,69,.25)',
                                        borderRadius: 8, padding: '8px 12px', flex: 1,
                                    }}>
                                        {auth.httpError || 'An unexpected error occurred.'}
                                </div>
                            </div>
                            <div className="d-flex flex-column w-100" style={{ gap: 8 }}>
                                <Button
                                    className="w-100 gn-login-try-again-btn"
                                    onClick={() => authClearError()}
                                >
                                    Try Again
                                </Button>
                                <Button
                                    className="w-100 gn-login-ea-btn"
                                    onClick={() => { authClearError(); onHide?.(); navigate(ROUTES.PATH.EARLY_ACCESS); }}
                                >
                                    Apply for Early Access
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <GoogleLogin
                            shape="pill"
                            size="large"
                            onSuccess={handleGoogleLoginCallback}
                            onError={handleGoogleLoginErrorCallback}
                        />
                    )}

                    <div className="text-center" style={{ color: '#6f6f6b', fontSize: 12, lineHeight: 1.6 }}>
                        <div>By signing in you agree to our</div>
                        <div>
                            <Link to={ROUTES.PATH.TERMS_OF_SERVICE} className="gn-login-modal-link">Terms of Service</Link>
                            {' · '}
                            <Link to={ROUTES.PATH.PRIVACY_POLICY} className="gn-login-modal-link">Privacy Policy</Link>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default withReduxAuth(LoginModal);